import { required, minValue, maxValue, helpers, requiredIf } from '@vuelidate/validators';
import FIMenu from './FIMenu';
import FIMenuLoanTerm from './FIMenuLoanTerm';

export const balloonValidationMessage = 'Balloon amount is required. \nSet this to 0 if not in use.';

export default class FIMenuLoanTerms {
	acceptedTerm?: number = null;
	line5limit?: any = null;
	additionalDownPayment?: boolean = null;
	/** Determines if the deal has a lender that allows balloon payments */
	isBalloon: boolean = false;
	maxDaysToFirstPayment?: any = null;
	selectedDaysToFirstPayment?: any = null;
	finalMonthlyPayment?: any = null;
	term1?: FIMenuLoanTerm = new FIMenuLoanTerm();
	term2?: FIMenuLoanTerm = new FIMenuLoanTerm();
	term3?: FIMenuLoanTerm = new FIMenuLoanTerm();

	static validationTypes = {
		TERMS: 'TERMS',
		DEFAULT: 'DEFAULT',
	};

	constructor(init?: Partial<FIMenuLoanTerms>) {
		if (init) {
			this.acceptedTerm = init?.acceptedTerm;
			this.line5limit = init?.line5limit;
			this.additionalDownPayment = init?.additionalDownPayment;
			this.isBalloon = init?.isBalloon ?? false;
			this.maxDaysToFirstPayment = init?.maxDaysToFirstPayment;
			this.selectedDaysToFirstPayment = init?.selectedDaysToFirstPayment;
			this.finalMonthlyPayment = init?.finalMonthlyPayment;
			this.term1 = new FIMenuLoanTerm(init?.term1);
			this.term2 = new FIMenuLoanTerm(init?.term2);
			this.term3 = new FIMenuLoanTerm(init?.term3);
		}
	}
	getAcceptedTerm() {
		if (this.acceptedTerm) {
			if (this.acceptedTerm === 1 && this.term1) return this.term1;
			if (this.acceptedTerm === 2 && this.term2) return this.term2;
			if (this.acceptedTerm === 3 && this.term3) return this.term3;
		}
		return null;
	}
	allTerms() {
		return [this.term1, this.term2, this.term3].filter(term => term.isChoice());
	}
	availableTerms() {
		return [this.term1, this.term2, this.term3].filter(term => term.isAvailable());
	}

	atLeastOneBalloonAmount() {
		return (
			(this.term1.balloonAmount != null && this.term1.balloonAmount > 0) ||
			(this.term2.balloonAmount != null && this.term2.balloonAmount > 0) ||
			(this.term3.balloonAmount != null && this.term3.balloonAmount > 0)
		);
	}

	resetBalloonAmounts() {
		this.term1.balloonAmount = null;
		this.term2.balloonAmount = null;
		this.term3.balloonAmount = null;
	}

	validation(fimenu: FIMenu, type = FIMenuLoanTerms.validationTypes.DEFAULT) {
		const data = {
			maxDaysToFirstPayment: {},
			finalMonthlyPayment: {},
			term1: {
				term: {},
				buyrate: {},
				sellrate: {},
				balloonAmount: {},
			},
			term2: {
				term: {},
				buyrate: {},
				sellrate: {},
				balloonAmount: {},
			},
			term3: {
				term: {},
				buyrate: {},
				sellrate: {},
				balloonAmount: {},
			},
		};

		switch (type) {
			case FIMenuLoanTerms.validationTypes.DEFAULT:
				return data;
			case FIMenuLoanTerms.validationTypes.TERMS:
				if (fimenu.isFinance()) {
					const t1BuyRateMax = fimenu.loanTerms.term1.sellrate ?? 0;
					const t2BuyRateMax = fimenu.loanTerms.term2.sellrate ?? 0;
					const t3BuyRateMax = fimenu.loanTerms.term3.sellrate ?? 0;

					data.maxDaysToFirstPayment = { required, minValue: minValue(30) };
					data.term1 = {
						term: { required, minValue: minValue(1) },
						buyrate: {
							required,
							minValue: minValue(t1BuyRateMax - 3),
							maxValue: maxValue(t1BuyRateMax),
						},
						sellrate: {
							required,
							minValue: minValue(fimenu.loanTerms.term1.buyrate),
						},
						balloonAmount: {
							required: helpers.withMessage(
								balloonValidationMessage,
								requiredIf(() => fimenu.loanTerms.isBalloon),
							),
						},
					};

					if (fimenu.loanTerms.term2.term) {
						data.term2.buyrate = {
							minValue: minValue(t2BuyRateMax - 3),
							maxValue: maxValue(t2BuyRateMax),
						};
						data.term2.sellrate = {
							required,
							minValue: minValue(fimenu.loanTerms.term2.buyrate),
						};
						data.term2.balloonAmount = {
							required: helpers.withMessage(
								balloonValidationMessage,
								requiredIf(() => fimenu.loanTerms.isBalloon),
							),
						};
					}

					if (fimenu.loanTerms.term3.term) {
						data.term3.buyrate = {
							minValue: minValue(t3BuyRateMax - 3),
							maxValue: maxValue(t3BuyRateMax),
						};
						data.term3.sellrate = {
							required,
							minValue: minValue(fimenu.loanTerms.term3.buyrate),
						};
						data.term3.balloonAmount = {
							required: helpers.withMessage(
								balloonValidationMessage,
								requiredIf(() => fimenu.loanTerms.isBalloon),
							),
						};
					}
				} else if (fimenu.isDraft()) {
					if (fimenu.loanTerms.term1.term) {
						data.term1.term = { minValue: minValue(0) };
						data.term1.sellrate = { minValue: minValue(0) };
						data.term1.buyrate = { minValue: minValue(0) };
					}

					if (fimenu.loanTerms.term2.term) {
						data.term2.term = { minValue: minValue(0) };
						data.term2.sellrate = { minValue: minValue(0) };
						data.term2.buyrate = { minValue: minValue(0) };
					}

					if (fimenu.loanTerms.term3.term) {
						data.term3.term = { minValue: minValue(0) };
						data.term3.sellrate = { minValue: minValue(0) };
						data.term3.buyrate = { minValue: minValue(0) };
					}
				}
				return data;
			default:
				return data;
		}
	}
}
